import React, {useState} from "react";

import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogProps,
    TextField,
    Typography,
} from "@mui/material";
import {LoadingDotBox} from "@/components/Shared/LoadingBox.tsx";
import {blue, grey} from "@mui/material/colors";

export interface ExtendedDialogProps extends DialogProps {
    cancelMessage?: string
    acceptMessage?: string
    closeMessage?: string
    validated?: boolean
    loading?: boolean
    disabledButtonConfirm?: boolean
    description?: string
    component?: React.FunctionComponent
    open: boolean
    close: () => void
    callback?: any
    typingValue: string
    renderChildren: any
    subtitle?: string
}

export default function ConfirmDialog({
                                          cancelMessage,
                                          acceptMessage,
                                          closeMessage,
                                          validated = true,
                                          loading = false,
                                          disabledButtonConfirm = false,
                                          component: Component,
                                          close,
                                          callback,
                                          typingValue,
                                          renderChildren,
                                          ...props
                                      }: ExtendedDialogProps) {
    const componentContext = {
        cancelMessage,
        acceptMessage,
        closeMessage,
        loading,
        validated,
        close,
        callback,
        ...props,
    };

    const [typingToConfirm, setTypingToConfirm] = useState("");
    const [isLoading, setLoading] = useState<boolean>(false);
    const isNotCorrectTyping =
        Boolean(typingValue) && typingToConfirm !== typingValue;

    async function handleSubmitAction() {
        if (typingValue) {
            if (typingValue !== typingToConfirm) {
                return;
            }
        }

        setLoading(true);

        if (callback) {
            await callback();
            setLoading(false);
            close();
        }
    }

    return (
        <Dialog
            open={true}
            scroll="paper"
            onClose={close}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
            fullWidth
            maxWidth={props.maxWidth}
            transitionDuration={0}
            sx={{
                ".MuiDialog-paper": {
                    borderRadius: 0,
                    boxShadow: '0 1px 1px 0 #001c244d, 1px 1px 1px 0 #001c2426, -1px 1px 1px 0 #001c2426',
                },
                ".MuiDialogActions-root": {
                    borderTop: '1px solid #EEEEEE'
                }
            }}
        >
            <Box sx={{p: 2, bgcolor: grey[50], borderBottom: `1px solid ${grey[200]}`}}>
                <Typography fontWeight={500}>{props.title}</Typography>
                {props.subtitle && (
                    <Typography fontSize={12} color={grey[800]}>{props.subtitle}</Typography>
                )}
            </Box>
            {Component ? (
                <React.Suspense fallback={
                    <DialogContent>
                        <LoadingDotBox sx={{py: 1, px: 2}}/>
                    </DialogContent>
                }>
                    <Component {...componentContext} />
                </React.Suspense>
            ) : (
                <>
                    <DialogContent>
                        <DialogContentText paragraph fontSize={14}>
                            {props.description}
                        </DialogContentText>

                        {typingValue && (
                            <>
                                <Typography fontSize={13} color={grey[700]} fontWeight={700}>
                                    Please type in the name to confirm
                                </Typography>
                                <TextField
                                    value={typingToConfirm}
                                    onChange={(e) => setTypingToConfirm(e.target.value)}
                                    placeholder={`type ${typingValue} to confirm`}
                                    size="small"
                                    sx={{
                                        minWidth: "100%",
                                        "& input": {fontSize: 13},
                                        mt: 1,
                                        ".MuiInputBase-root": {
                                            borderRadius: 0,
                                        },
                                    }}
                                    inputProps={{autoComplete: "false"}}
                                />
                            </>
                        )}

                        {renderChildren && renderChildren()}
                    </DialogContent>

                    {callback && validated ? (
                        <DialogActions>
                            <Button size="small" onClick={close} sx={{
                                py: 0.75,
                                px: 1,
                                borderRadius: 0,
                                bgcolor: grey[50],
                            }}>
                                <Typography component="span" fontSize={14} fontWeight={400} textTransform="capitalize">
                                    {cancelMessage || "Cancel"}
                                </Typography>
                            </Button>
                            <Button
                                size="small"
                                onClick={handleSubmitAction}
                                // variant="contained"
                                // color="primary"
                                disabled={
                                    isLoading ||
                                    loading ||
                                    disabledButtonConfirm ||
                                    isNotCorrectTyping
                                }
                                startIcon={
                                    (isLoading || loading) && <CircularProgress size={20}/>
                                }
                                sx={{
                                    py: 0.75,
                                    px: 1,
                                    bgcolor: blue[900],
                                    borderRadius: 0,
                                    color: 'white',
                                    "&:hover": {
                                        bgcolor: blue[800],
                                        color: 'white',
                                    },
                                    "&:disabled": {
                                        bgcolor: grey[200]
                                    }
                                }}
                            >
                                <Typography component="span" fontSize={14} fontWeight={400} textTransform="capitalize">
                                    {acceptMessage || "Accept"}
                                </Typography>
                            </Button>
                        </DialogActions>
                    ) : (
                        <DialogActions>
                            <Button size="small" onClick={close} sx={{
                                py: 0.75,
                                px: 1,
                                borderRadius: 0,
                                bgcolor: grey[100],
                            }}>
                                <Typography component="span" fontSize={14} fontWeight={400} textTransform="capitalize">
                                    {closeMessage || "Close"}
                                </Typography>
                            </Button>
                        </DialogActions>
                    )}
                </>
            )}
        </Dialog>
    );
}
